import React, { useEffect, useState } from "react";
import { publicApi } from "./lib/api";
import { Route, Routes, BrowserRouter, Navigate, useLocation, Link } from "react-router-dom";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import About from "./pages/About";
import WhatWeDo from "./pages/WhatWeDo";
import Organisations from "./pages/Organisations";
import DiasporaNetwork from "./pages/DiasporaNetwork";
import Partner from "./pages/Partner";
import Webinars from "./pages/Webinars";
import WebinarDetail from "./pages/WebinarDetail";
import Join from "./pages/Join";
import Donate from "./pages/Donate";
import ApplyForSupport from "./pages/ApplyForSupport";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import Activate from "./pages/Activate";
import MemberLogin from "./pages/MemberLogin";
import { ForgotPassword, ResetPassword } from "./pages/PasswordReset";
import MemberHome from "./pages/member/MemberHome";
import Profile from "./pages/member/Profile";
import Directory from "./pages/member/Directory";
import MemberProfileView from "./pages/member/MemberProfileView";
import { MessagesList, MessageThread } from "./pages/member/Messages";
import { EventsList, EventDetail } from "./pages/member/Events";
import { Opportunities, Account } from "./pages/member/OpportunitiesAccount";
import MemberNetworkUpdates from "./pages/member/NetworkUpdates";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import PreLaunch from "./pages/PreLaunch";
import JoinWaitlisted from "./pages/JoinWaitlisted";
import Wordmark from "./components/Wordmark";
import { usePageSEO } from "./hooks/usePageSEO";
import { CookieConsentProvider, PublicConsent } from "./components/CookieConsent";
import { MemberAuthProvider } from "./context/MemberAuthContext";
import { Toaster } from "./components/ui/toaster";
import "./App.css";

const FullSite = () => (
  <Layout>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/what-we-do" element={<WhatWeDo />} />
      <Route path="/organisations" element={<Organisations />} />
      <Route path="/diaspora" element={<DiasporaNetwork />} />
      <Route path="/partner" element={<Partner />} />
      <Route path="/webinars" element={<Webinars />} />
      <Route path="/webinars/:id" element={<WebinarDetail />} />
      <Route path="/join" element={<Join />} />
      <Route path="/join/waitinglist" element={<JoinWaitlisted />} />
      <Route path="/donate" element={<Donate />} />
      <Route path="/apply-for-support" element={<ApplyForSupport />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/activate/:token" element={<Activate />} />
      <Route path="/login" element={<MemberLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/member" element={<MemberHome />} />
      <Route path="/member/profile" element={<Profile />} />
      <Route path="/member/directory" element={<Directory />} />
      <Route path="/member/directory/:id" element={<MemberProfileView />} />
      <Route path="/member/messages" element={<MessagesList />} />
      <Route path="/member/messages/:id" element={<MessageThread />} />
      <Route path="/member/events" element={<EventsList />} />
      <Route path="/member/events/:id" element={<EventDetail />} />
      <Route path="/member/opportunities" element={<Opportunities />} />
      <Route path="/member/updates" element={<MemberNetworkUpdates />} />
      <Route path="/member/updates/:id" element={<MemberNetworkUpdates />} />
      <Route path="/member/account" element={<Account />} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Layout>
);

const PreLaunchShell = ({ children }) => {
  const location = useLocation();
  usePageSEO(location.pathname);
  return (
  <div style={{ background: 'var(--beige)' }} className="min-h-screen">
    <header
      className="sticky top-0 z-40 backdrop-blur"
      style={{ background: 'rgba(242,232,214,0.85)', borderBottom: '1px solid rgba(18,52,40,0.08)' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Wordmark size={32} />
        <div className="flex items-center gap-5 text-[13px]" style={{ color: 'rgba(18,52,40,0.75)' }}>
          <Link to="/webinars" className="link-underline" data-testid="shell-masterclasses-link">Masterclasses</Link>
          <a href="/login" className="link-underline" data-testid="shell-signin-link">Member Sign In</a>
        </div>
      </div>
    </header>
    {children}
    <footer style={{ background: 'var(--forest)', color: 'rgba(255,246,226,0.85)' }} className="mt-16">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <Wordmark size={30} color="var(--gold)" />
            <p className="mt-3 text-[13px] leading-relaxed max-w-[300px]" style={{ color: 'rgba(255,246,226,0.7)' }}>
              Building Nigeria&rsquo;s future workforce.
            </p>
          </div>
          <div>
            <div className="section-eyebrow mb-3" style={{ color: 'rgba(255,246,226,0.55)' }}>Contact</div>
            <a
              href="mailto:hello@vemoyefoundation.org"
              className="link-underline text-[13px]"
              style={{ color: 'var(--cream)' }}
            >
              hello@vemoyefoundation.org
            </a>
          </div>
          <div>
            <div className="section-eyebrow mb-3" style={{ color: 'rgba(255,246,226,0.55)' }}>Legal</div>
            <div className="flex flex-col gap-1.5 text-[13px]">
              <a href="/privacy" className="link-underline" style={{ color: 'var(--cream)' }}>Privacy Policy</a>
              <a href="/terms" className="link-underline" style={{ color: 'var(--cream)' }}>Terms &amp; Conditions</a>
            </div>
          </div>
        </div>
        <div
          className="pt-5 flex flex-wrap items-center justify-between gap-3 text-[12px] border-t"
          style={{ borderColor: 'rgba(255,246,226,0.12)', color: 'rgba(255,246,226,0.55)' }}
        >
          <div>© {new Date().getFullYear()} Vemoye Foundation · Registered charity in England &amp; Wales, No. 1188086.</div>
        </div>
      </div>
    </footer>
    <PublicConsent />
  </div>
  );
};

const PreLaunchRoutes = () => (
  <Routes>
    <Route path="/" element={<PreLaunch />} />
    <Route path="/join" element={<PreLaunchShell><Join /></PreLaunchShell>} />
    <Route path="/join/waitinglist" element={<PreLaunchShell><JoinWaitlisted /></PreLaunchShell>} />
    <Route path="/privacy" element={<PreLaunchShell><Privacy /></PreLaunchShell>} />
    <Route path="/terms" element={<PreLaunchShell><Terms /></PreLaunchShell>} />
    <Route path="/webinars" element={<PreLaunchShell><Webinars /></PreLaunchShell>} />
    <Route path="/webinars/:id" element={<PreLaunchShell><WebinarDetail /></PreLaunchShell>} />
    <Route path="/login" element={<MemberLogin />} />
    <Route path="/forgot-password" element={<ForgotPassword />} />
    <Route path="/reset-password/:token" element={<ResetPassword />} />
    <Route path="/activate/:token" element={<Activate />} />
    {/* Member portal is accessible in pre-launch so approved members can log in */}
    <Route path="/member" element={<MemberHome />} />
    <Route path="/member/profile" element={<Profile />} />
    <Route path="/member/directory" element={<Directory />} />
    <Route path="/member/directory/:id" element={<MemberProfileView />} />
    <Route path="/member/messages" element={<MessagesList />} />
    <Route path="/member/messages/:id" element={<MessageThread />} />
    <Route path="/member/events" element={<EventsList />} />
    <Route path="/member/events/:id" element={<EventDetail />} />
    <Route path="/member/opportunities" element={<Opportunities />} />
    <Route path="/member/updates" element={<MemberNetworkUpdates />} />
    <Route path="/member/updates/:id" element={<MemberNetworkUpdates />} />
    <Route path="/member/account" element={<Account />} />
    <Route path="/admin" element={<AdminLogin />} />
    <Route path="/admin/dashboard" element={<AdminDashboard />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

function App() {
  const [prelaunch, setPrelaunch] = useState(null);
  useEffect(() => {
    publicApi.get("/settings").then(({ data }) => setPrelaunch(!!data.prelaunch_mode)).catch(() => setPrelaunch(false));
  }, []);
  if (prelaunch === null) return <div style={{ background: '#f4efe4', minHeight: '100vh' }} />;
  return (
    <div className="App">
      <BrowserRouter>
        <MemberAuthProvider>
          <CookieConsentProvider>
            {prelaunch ? <PreLaunchRoutes /> : <FullSite />}
            <Toaster />
          </CookieConsentProvider>
        </MemberAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
